import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { AssignPersonDialog } from "../components/personal/AssignPersonDialog";
import { PersonAssignmentsDialog } from "../components/personal/PersonAssignmentsDialog";
import { UserCog, Search, Eye, EyeOff, Briefcase, ListChecks, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PersonalPage() {
  const { token } = useAuth();
  const [people, setPeople] = useState([]);
  const [stats, setStats] = useState(null);
  const [filtros, setFiltros] = useState({ zonas: [], titulaciones: [] });
  const [search, setSearch] = useState("");
  const [zona, setZona] = useState("all");
  const [titulacion, setTitulacion] = useState("all");
  const [loading, setLoading] = useState(true);
  const [revealedDni, setRevealedDni] = useState({});
  const [savingCoste, setSavingCoste] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [viewAssignmentsTarget, setViewAssignmentsTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (zona !== "all") params.zona = zona;
      if (titulacion !== "all") params.titulacion = titulacion;
      const [list, statsData] = await Promise.all([api.listPersonal(token, params), api.getPersonalStats(token)]);
      setPeople(list);
      setStats(statsData);
    } catch (e) {
      toast.error("Error al cargar el directorio de Personal");
    } finally {
      setLoading(false);
    }
  }, [token, search, zona, titulacion]);

  useEffect(() => { api.getPersonalFiltros(token).then(setFiltros).catch(() => {}); }, [token]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const toggleDni = async (person) => {
    if (revealedDni[person.id]) {
      setRevealedDni((r) => { const c = { ...r }; delete c[person.id]; return c; });
      return;
    }
    try {
      const { nif_dni } = await api.revealPersonalDni(token, person.id);
      setRevealedDni((r) => ({ ...r, [person.id]: nif_dni }));
    } catch (e) {
      toast.error("No se pudo revelar el DNI");
    }
  };

  const saveCoste = async (person, value) => {
    setSavingCoste(person.id);
    try {
      const updated = await api.updatePersonal(token, person.id, { coste_hora: value === "" ? null : parseFloat(value) });
      setPeople((prev) => prev.map((p) => (p.id === person.id ? { ...p, coste_hora: updated.coste_hora } : p)));
      setStats(await api.getPersonalStats(token));
    } catch (e) {
      toast.error("Error al guardar el coste/hora");
    } finally {
      setSavingCoste(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="personal-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2"><UserCog className="w-6 h-6 text-emerald-700" />Personal</h1>
        <p className="text-sm text-muted-foreground">Personal disponible en Valoriza — zonas, titulaciones y asignación a proyectos con horas y coste. Acceso restringido.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="personal-stats-cards">
          <StatCard label="Personas" value={stats.total_personas} />
          <StatCard label="Zonas" value={stats.zonas} />
          <StatCard label="Titulaciones" value={stats.titulaciones} />
          <StatCard label="Con coste/hora" value={stats.con_coste_asignado} />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Buscar por nombre, apellidos o nº personal..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="personal-search-input" />
        </div>
        <Select value={zona} onValueChange={setZona}>
          <SelectTrigger className="sm:w-56" data-testid="personal-zona-filter"><SelectValue placeholder="Zona" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Todas las zonas</SelectItem>
            {filtros.zonas.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={titulacion} onValueChange={setTitulacion}>
          <SelectTrigger className="sm:w-64" data-testid="personal-titulacion-filter"><SelectValue placeholder="Titulación" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Todas las titulaciones</SelectItem>
            {filtros.titulaciones.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : people.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="personal-empty-state">Sin resultados.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="personal-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Apellidos, Nombre</TableHead>
                    <TableHead>Nº Personal</TableHead>
                    <TableHead>DNI</TableHead>
                    <TableHead>Zona</TableHead>
                    <TableHead>Titulación</TableHead>
                    <TableHead>Coste/hora (€)</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {people.map((p) => (
                    <TableRow key={p.id} data-testid={`personal-row-${p.id}`}>
                      <TableCell className="font-medium whitespace-nowrap">{p.apellidos}, {p.nombre}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.numero_personal}</TableCell>
                      <TableCell>
                        <button className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => toggleDni(p)} data-testid={`personal-dni-toggle-${p.id}`}>
                          {revealedDni[p.id] ? <><Eye className="w-3.5 h-3.5" />{revealedDni[p.id]}</> : <><EyeOff className="w-3.5 h-3.5" />Ver DNI</>}
                        </button>
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs whitespace-nowrap">{p.zona_nombre || "—"}</Badge></TableCell>
                      <TableCell className="text-xs max-w-[220px] truncate" title={p.titulacion}>{p.titulacion || "—"}</TableCell>
                      <TableCell>
                        <Input
                          type="number" step="0.5" defaultValue={p.coste_hora ?? ""} className="w-24 h-8"
                          onBlur={(e) => e.target.value !== String(p.coste_hora ?? "") && saveCoste(p, e.target.value)}
                          disabled={savingCoste === p.id} data-testid={`personal-coste-input-${p.id}`}
                        />
                      </TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button size="sm" variant="outline" className="mr-1.5" onClick={() => setAssignTarget(p)} data-testid={`personal-assign-btn-${p.id}`}>
                          <Briefcase className="w-3.5 h-3.5 mr-1.5" />Asignar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setViewAssignmentsTarget(p)} data-testid={`personal-view-assignments-btn-${p.id}`}>
                          <ListChecks className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AssignPersonDialog open={!!assignTarget} onOpenChange={(v) => !v && setAssignTarget(null)} person={assignTarget} onSaved={load} />
      <PersonAssignmentsDialog open={!!viewAssignmentsTarget} onOpenChange={(v) => !v && setViewAssignmentsTarget(null)} person={viewAssignmentsTarget} />
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <Card><CardContent className="p-3 text-center">
      <div className="text-2xl font-bold">{value ?? 0}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </CardContent></Card>
  );
}
